import {
  ACCOUNT_PAYABLE_MENU,
  ACCOUNTS_PAYABLE_MENU,
  ApNavGroup,
  CompanyPageSubMenu,
  CompanyPagePermission,
  CompanyPermission,
  EXPENSE_MANAGEMENT_MENU,
  ExpenseNavGroup,
  HOMEPAGE_MENU,
  MEAL_DYNAMICS_AI_MENU,
  PageNavGroup,
  VENDORS_MENU,
  VendorNavGroup,
} from '../models/company-permission.model';
import {
  getVendorListTabSubMenus,
  sortVendorNavSubMenusForDesign,
  VENDOR_TABS_HIDDEN_ROLE_IDS,
} from './vendor-list-tab.util';
import { StorageKey } from '../models/common';
import { RoleConstant } from '../models/role-constant';
import { canUserAccessCollatedPayment } from './ap-manager-bypass.util';

/** Roles whose AP sidebar is driven by /my API + localStorage. */
export const AP_API_PERMISSION_ROLE_IDS: readonly string[] = [
  RoleConstant.companyAdminId,
  RoleConstant.accountsPayableCreator,
  RoleConstant.accountsPayableManager1,
  RoleConstant.accountsPayableManager2,
];

/**
 * Roles that may receive Expense actionPermissions / pagePermissions from /my
 * are not hardcoded — any role with stored companyPermissions is evaluated from BE.
 */

/** Legacy hardcoded sidebar roles (includes Global Admin). */
const AP_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  ...AP_API_PERMISSION_ROLE_IDS,
];

const AP_LIST_EXCLUDED_SEGMENTS = [
  'vendor-payment-list',
  'collated-payment',
  'new',
] as const;

export function isApApiPermissionRole(
  roleId: string | null | undefined
): boolean {
  return Boolean(roleId && AP_API_PERMISSION_ROLE_IDS.includes(roleId));
}

/**
 * Persist /my companyPermissions for the selected role whenever present.
 * Expense + Accounts Payable (and future modules) read from this store.
 */
export function shouldStoreCompanyPermissionsForRole(
  roleId: string | null | undefined,
  companyPermissions?: CompanyPermission[] | null
): boolean {
  if (!roleId) {
    return false;
  }

  // Always store when /my returned companyPermissions (including []).
  if (companyPermissions !== undefined && companyPermissions !== null) {
    return true;
  }

  return isApApiPermissionRole(roleId);
}

export function resolveActiveCompanyId(
  userRoles: Array<{ company?: { id?: string } }> | null | undefined,
  companyIdFromContext: string | null | undefined
): string | null {
  return (
    userRoles?.[0]?.company?.id ??
    companyIdFromContext ??
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('selectedCompanyId')
      : null)
  );
}

export function getCompanyPermissionForCompany(
  companyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): CompanyPermission | null {
  if (!companyPermissions?.length || !companyId) {
    return null;
  }

  return (
    companyPermissions.find((permission) => permission.companyId === companyId) ??
    null
  );
}

/** BE may send "Accounts Payable" and/or "Account Payable"; both count for access. */
export function isAccountsPayableMenuName(
  menu: string | null | undefined
): boolean {
  const normalized = (menu ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  return (
    normalized === 'accounts payable' || normalized === 'account payable'
  );
}

/** BE may send "Homepage" or "HomePage" for the home dashboard menu. */
export function isHomepageMenuName(menu: string | null | undefined): boolean {
  const normalized = (menu ?? '').trim().toLowerCase().replace(/\s+/g, '');
  return normalized === 'homepage';
}

export function getHomepagePagePermission(
  companyPermission: CompanyPermission | null | undefined
): CompanyPagePermission | null {
  return (
    companyPermission?.pagePermissions?.find(
      (pagePermission) =>
        isHomepageMenuName(pagePermission.menu) &&
        (pagePermission.subMenus?.length ?? 0) > 0
    ) ?? null
  );
}

/** BE may send "Meal Dynamics AI" or typo "Meal Dynamnics AI". */
export function isMealDynamicsAiMenuName(menu: string | null | undefined): boolean {
  const token = (menu ?? '').trim().toLowerCase().replace(/[^a-z]/g, '');
  return token === 'mealdynamicsai' || token === 'mealdynamnicsai';
}

export function getMealDynamicsAiPagePermission(
  companyPermission: CompanyPermission | null | undefined
): CompanyPagePermission | null {
  return (
    companyPermission?.pagePermissions?.find(
      (pagePermission) =>
        isMealDynamicsAiMenuName(pagePermission.menu) &&
        (pagePermission.subMenus?.length ?? 0) > 0
    ) ?? null
  );
}

/** All AP pagePermissions entries (singular + plural), preserving BE order. */
export function getApPagePermissions(
  companyPermission: CompanyPermission | null | undefined
): CompanyPagePermission[] {
  return (companyPermission?.pagePermissions ?? []).filter((pagePermission) =>
    isAccountsPayableMenuName(pagePermission.menu)
  );
}

export function getPagePermission(
  companyPermission: CompanyPermission | null | undefined,
  menu: string
): CompanyPagePermission | null {
  // Exact menu match first so "Account Payable" ≠ "Accounts Payable".
  const exact =
    companyPermission?.pagePermissions?.find(
      (pagePermission) => pagePermission.menu === menu
    ) ?? null;
  if (exact) {
    return exact;
  }

  if (menu === HOMEPAGE_MENU || isHomepageMenuName(menu)) {
    return getHomepagePagePermission(companyPermission);
  }

  if (menu === MEAL_DYNAMICS_AI_MENU || isMealDynamicsAiMenuName(menu)) {
    return getMealDynamicsAiPagePermission(companyPermission);
  }

  if (!isAccountsPayableMenuName(menu)) {
    return null;
  }

  // Fallback: first AP menu when caller used a canonical AP label.
  return getPrimaryApPagePermission(companyPermission);
}

export function getPrimaryApPagePermission(
  companyPermission: CompanyPermission | null | undefined
): CompanyPagePermission | null {
  return (
    getApPagePermissions(companyPermission).find(
      (pagePermission) => pagePermission.subMenus?.length
    ) ?? null
  );
}

export function getMenuSubMenusFromApi(
  companyPermission: CompanyPermission | null | undefined,
  menu?: string
): CompanyPageSubMenu[] {
  const resolvedMenu = menu ?? ACCOUNTS_PAYABLE_MENU;

  // Any AP label: return subMenus from every AP menu (paths may differ).
  if (isAccountsPayableMenuName(resolvedMenu)) {
    return getApPagePermissions(companyPermission).flatMap(
      (pagePermission) => pagePermission.subMenus ?? []
    );
  }

  return getPagePermission(companyPermission, resolvedMenu)?.subMenus ?? [];
}

export function hasPageUrlInMenu(
  companyPermission: CompanyPermission | null | undefined,
  menu: string,
  pageUrl: string
): boolean {
  const normalizedPageUrl = normalizePageUrl(pageUrl);
  return getMenuSubMenusFromApi(companyPermission, menu).some(
    (subMenu) => normalizePageUrl(subMenu.pageUrl) === normalizedPageUrl
  );
}

/** Path only (query/hash stripped) for route matching and access checks. */
export function normalizePageUrl(pageUrl: string): string {
  return pageUrl
    .replace(/^\/+/, '')
    .split('?')[0]
    .split('#')[0]
    .toLowerCase();
}

export function normalizeRoutePath(pathname: string): string {
  return pathname.replace(/^\//, '').toLowerCase().replace(/\/$/, '') || '/';
}

/** Split BE pageUrl like `accounts-payable/list?overview=` into path + query. */
export function parsePageUrlParts(pageUrl: string): {
  path: string;
  queryParams: Record<string, string> | null;
} {
  const withoutHash = pageUrl.split('#')[0];
  const qIndex = withoutHash.indexOf('?');
  if (qIndex === -1) {
    return {
      path: withoutHash.replace(/^\/+/, ''),
      queryParams: null,
    };
  }

  const path = withoutHash.slice(0, qIndex).replace(/^\/+/, '');
  const params = new URLSearchParams(withoutHash.slice(qIndex + 1));
  const queryParams: Record<string, string> = {};
  params.forEach((value, key) => {
    queryParams[key] = value;
  });

  return {
    path,
    queryParams: Object.keys(queryParams).length ? queryParams : null,
  };
}

export function getPageUrlQueryParams(
  pageUrl: string
): Record<string, string> | null {
  return parsePageUrlParts(pageUrl).queryParams;
}

export const AP_SUBMENU_BULK_UPLOAD_PAGE_URL = 'BulkAPUpload';

export function isApSubMenuAction(pageUrl: string): boolean {
  const normalized = normalizePageUrl(pageUrl);
  return normalized === normalizePageUrl(AP_SUBMENU_BULK_UPLOAD_PAGE_URL);
}

export function isApSubMenuRoute(pageUrl: string): boolean {
  return !isApSubMenuAction(pageUrl);
}

export function toRouterPath(pageUrl: string): string {
  const pathOnly = pageUrl.split('?')[0].split('#')[0];
  return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
}

export function isApListRoutePath(normalizedPath: string): boolean {
  if (!normalizedPath.startsWith('accounts-payable')) {
    return false;
  }

  return !AP_LIST_EXCLUDED_SEGMENTS.some((segment) =>
    normalizedPath.includes(segment)
  );
}

export function isApSubMenuRouteActive(
  pageUrl: string,
  currentPathname: string,
  currentQuery?: URLSearchParams | null
): boolean {
  if (isApSubMenuAction(pageUrl)) {
    return false;
  }

  const { path: pagePath, queryParams } = parsePageUrlParts(pageUrl);
  const normalizedPageUrl = pagePath.toLowerCase();
  const normalizedCurrentPath = normalizeRoutePath(currentPathname);

  const pathMatches =
    normalizedPageUrl === 'accounts-payable/list'
      ? isApListRoutePath(normalizedCurrentPath)
      : normalizedCurrentPath === normalizedPageUrl ||
        normalizedCurrentPath.startsWith(`${normalizedPageUrl}/`);

  if (!pathMatches) {
    return false;
  }

  if (!queryParams) {
    return true;
  }

  // Filter tabs (approval=, overview=, …) only highlight on the list route itself.
  if (
    normalizedPageUrl === 'accounts-payable/list' &&
    normalizedCurrentPath !== 'accounts-payable/list' &&
    normalizedCurrentPath !== 'accounts-payable'
  ) {
    return false;
  }

  if (!currentQuery) {
    return false;
  }

  return Object.keys(queryParams).every((key) => currentQuery.has(key));
}

/**
 * Preferred landing when an AP sidebar parent is clicked:
 * 1. Overview tab (if present)
 * 2. Main AP list (`accounts-payable/list` / "Account Payable" listing)
 * 3. First routable submenu
 */
export function findPreferredApLandingSubMenu(
  subMenus: CompanyPageSubMenu[] | null | undefined
): CompanyPageSubMenu | null {
  const routes = (subMenus ?? []).filter((subMenu) =>
    isApSubMenuRoute(subMenu.pageUrl)
  );
  if (!routes.length) {
    return null;
  }

  const overview = routes.find((subMenu) => {
    if ((subMenu.page ?? '').toLowerCase().includes('overview')) {
      return true;
    }
    const { queryParams } = parsePageUrlParts(subMenu.pageUrl);
    return Boolean(
      queryParams &&
        Object.prototype.hasOwnProperty.call(queryParams, 'overview')
    );
  });
  if (overview) {
    return overview;
  }

  // Accounts Payable parent → open the AP listing, not Vendor Payments / other first items.
  const apList = routes.find((subMenu) => isApMainListSubMenu(subMenu));
  if (apList) {
    return apList;
  }

  return routes[0];
}

/** Main AP bills/list entry (not vendor-payment-list / collated-payment). */
export function isApMainListSubMenu(subMenu: CompanyPageSubMenu): boolean {
  const path = normalizePageUrl(subMenu.pageUrl);
  if (path === 'accounts-payable/list') {
    return true;
  }

  const page = (subMenu.page ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  return (
    page === 'account payable' ||
    page === 'accounts payable' ||
    page === 'account payable list' ||
    page === 'accounts payable list' ||
    page.includes('all bills')
  );
}

export function resolveApPageTitleFromSubMenus(
  pathname: string,
  subMenus: CompanyPageSubMenu[] | undefined,
  currentQuery?: URLSearchParams | null
): string | null {
  if (!subMenus?.length) {
    return null;
  }

  const normalizedPath = normalizeRoutePath(pathname.split('?')[0]);

  if (currentQuery) {
    const queryMatch = subMenus.find((subMenu) => {
      if (!isApSubMenuRoute(subMenu.pageUrl)) {
        return false;
      }
      const { path, queryParams } = parsePageUrlParts(subMenu.pageUrl);
      if (!queryParams || path.toLowerCase() !== normalizedPath) {
        return false;
      }
      return Object.keys(queryParams).every((key) => currentQuery.has(key));
    });
    if (queryMatch) {
      return queryMatch.page;
    }
  }

  const exactMatch = subMenus.find(
    (subMenu) =>
      isApSubMenuRoute(subMenu.pageUrl) &&
      normalizePageUrl(subMenu.pageUrl) === normalizedPath &&
      !getPageUrlQueryParams(subMenu.pageUrl)
  );
  if (exactMatch) {
    return exactMatch.page;
  }

  const listSubMenu = subMenus.find(
    (subMenu) => normalizePageUrl(subMenu.pageUrl) === 'accounts-payable/list'
  );
  if (listSubMenu && isApListRoutePath(normalizedPath)) {
    return listSubMenu.page;
  }

  return null;
}

function isAccountPayableSidebarMenuName(
  menu: string | null | undefined
): boolean {
  const normalized = (menu ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  return normalized === ACCOUNT_PAYABLE_MENU.toLowerCase();
}

/** Sidebar groups: only singular BE "Account Payable" (not plural "Accounts Payable"). */
function getApNavGroupsFromApi(
  companyPermission: CompanyPermission | null | undefined
): ApNavGroup[] {
  return getApPagePermissions(companyPermission)
    .filter(
      (pagePermission) =>
        isAccountPayableSidebarMenuName(pagePermission.menu) &&
        (pagePermission.subMenus?.length ?? 0) > 0
    )
    .map((pagePermission) => ({
      menu: pagePermission.menu,
      subMenus: pagePermission.subMenus ?? [],
    }));
}

function getApNavGroupLegacy(
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean
): ApNavGroup | null {
  if (!hasAccountsPayableSidebarLegacy(selectedRoleId)) {
    return null;
  }

  const subMenus = getAccountsPayableSubMenusLegacy(
    selectedRoleId,
    bypassApManager1
  );
  if (!subMenus.length) {
    return null;
  }

  return {
    menu: ACCOUNTS_PAYABLE_MENU,
    subMenus,
  };
}

export function hasAccountsPayableSidebarLegacy(
  selectedRoleId: string | null | undefined
): boolean {
  if (!selectedRoleId) {
    return false;
  }

  return AP_SIDEBAR_ROLE_IDS.includes(selectedRoleId);
}

export function getAccountsPayableSubMenusLegacy(
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean
): CompanyPageSubMenu[] {
  if (!hasAccountsPayableSidebarLegacy(selectedRoleId)) {
    return [];
  }

  const subMenus: CompanyPageSubMenu[] = [
    {
      page: 'Accounts Payable List',
      pageUrl: 'accounts-payable/list',
    },
    {
      page: 'Vendor Payment List',
      pageUrl: 'accounts-payable/vendor-payment-list',
    },
  ];

  if (selectedRoleId === RoleConstant.accountsPayableCreator) {
    subMenus.splice(1, 0, {
      page: 'Bulk AP Request',
      pageUrl: AP_SUBMENU_BULK_UPLOAD_PAGE_URL,
    });
  }

  if (canUserAccessCollatedPayment(selectedRoleId, bypassApManager1)) {
    subMenus.push({
      page: 'Collated Payment',
      pageUrl: 'accounts-payable/collated-payment',
    });
  }

  return subMenus;
}

export function usesApiPagePermissions(
  companyPermission: CompanyPermission | null | undefined
): boolean {
  // Empty [] still means BE is driving page access (deny-all until menus are granted).
  return Array.isArray(companyPermission?.pagePermissions);
}

/** True when any menu's subMenus include this pageUrl. */
export function hasPageUrlInAnyMenu(
  companyPermission: CompanyPermission | null | undefined,
  pageUrl: string
): boolean {
  const normalizedPageUrl = normalizePageUrl(pageUrl);
  return (companyPermission?.pagePermissions ?? []).some((pagePermission) =>
    (pagePermission.subMenus ?? []).some(
      (subMenu) => normalizePageUrl(subMenu.pageUrl) === normalizedPageUrl
    )
  );
}

/**
 * Sidebar page visibility from BE pagePermissions.
 * - pagePermissions missing/undefined: fall back to legacyAllowed (role allowlists)
 * - pagePermissions lists the URL: allow
 * - pagePermissions present but URL omitted: fall back to legacyAllowed so role-gated
 *   modules (vendors, users, stores, …) keep working when BE only ships AP/Expense menus
 */
export function resolveSidebarPageUrlAllowed(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  pageUrl: string,
  legacyAllowed: boolean
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  if (!usesApiPagePermissions(companyPermission)) {
    return legacyAllowed;
  }

  if (hasPageUrlInAnyMenu(companyPermission, pageUrl)) {
    return true;
  }

  return legacyAllowed;
}

/**
 * BE-only page visibility (Organizations): when pagePermissions are present, only the URLs BE lists are allowed;
 * there is no role-allowlist fallback. legacyAllowed applies only when pagePermissions are missing.
 */
export function resolveBePageUrlAllowed(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  pageUrl: string,
  legacyAllowed: boolean
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  if (!usesApiPagePermissions(companyPermission)) {
    return legacyAllowed;
  }

  return hasPageUrlInAnyMenu(companyPermission, pageUrl);
}

/** Primary list pageUrl used to gate each sidebar module when pagePermissions is BE-driven. */
export const SIDEBAR_MODULE_PAGE_URLS: Record<string, string> = {
  repair: 'repairs-and-maintenance/list',
  approvalProcess: 'approval-processes/list',
  expense: 'expenses/list',
  asset: 'asset-management/list',
  company: 'companies/list',
  storeGroup: 'store-groups/list',
  store: 'stores/list',
  user: 'users/list',
  roles: 'roles/list',
  vendors: 'vendors/list',
};

export function readApRolePermissionsMap(): Record<string, CompanyPermission[]> {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  try {
    const raw = localStorage.getItem(StorageKey.apRoleCompanyPermissions);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function storeApRoleCompanyPermissions(
  roleId: string,
  companyPermissions: CompanyPermission[] | undefined
): void {
  if (
    !shouldStoreCompanyPermissionsForRole(roleId, companyPermissions) ||
    typeof localStorage === 'undefined'
  ) {
    return;
  }

  const map = readApRolePermissionsMap();
  map[roleId] = companyPermissions ?? [];
  localStorage.setItem(
    StorageKey.apRoleCompanyPermissions,
    JSON.stringify(map)
  );
}

export function getStoredApRoleCompanyPermissions(
  roleId: string | null | undefined
): CompanyPermission[] | null {
  if (!roleId) {
    return null;
  }

  const map = readApRolePermissionsMap();
  return map[roleId] ?? null;
}

function getPageNavGroupFromApi(
  companyPermission: CompanyPermission | null | undefined,
  menu: string
): PageNavGroup | null {
  const pagePermission = getPagePermission(companyPermission, menu);
  if (!pagePermission?.subMenus?.length) {
    return null;
  }

  return {
    menu: pagePermission.menu,
    subMenus: pagePermission.subMenus,
  };
}

/** Homepage sidebar — BE pagePermissions only. */
export function getHomepageNavGroup(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): PageNavGroup | null {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  const pagePermission = getHomepagePagePermission(companyPermission);
  if (!pagePermission?.subMenus?.length) {
    return null;
  }

  return {
    menu: pagePermission.menu,
    subMenus: pagePermission.subMenus,
  };
}

/** Meal Dynamics AI sidebar — BE pagePermissions only. */
export function getMealDynamicsAiNavGroup(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): PageNavGroup | null {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  const pagePermission = getMealDynamicsAiPagePermission(companyPermission);
  if (!pagePermission?.subMenus?.length) {
    return null;
  }

  return {
    menu: pagePermission.menu,
    subMenus: pagePermission.subMenus,
  };
}

function getExpenseNavGroupFromApi(
  companyPermission: CompanyPermission | null | undefined
): ExpenseNavGroup | null {
  const pagePermission = getPagePermission(
    companyPermission,
    EXPENSE_MANAGEMENT_MENU
  );
  if (!pagePermission?.subMenus?.length) {
    return null;
  }

  return {
    menu: pagePermission.menu,
    subMenus: pagePermission.subMenus,
  };
}

/** Expense sidebar — BE pagePermissions only (no role allowlist fallback). */
export function getExpenseNavGroup(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  _selectedRoleId?: string | null | undefined
): ExpenseNavGroup | null {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  return getExpenseNavGroupFromApi(companyPermission);
}

export function hasExpenseSidebar(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId?: string | null | undefined
): boolean {
  return Boolean(
    getExpenseNavGroup(storedCompanyPermissions, companyId, selectedRoleId)
      ?.subMenus.length
  );
}

export function isExpenseSubMenuRoute(pageUrl: string): boolean {
  const normalized = normalizePageUrl(pageUrl);
  return normalized.length > 0;
}

export function isExpenseSubMenuRouteActive(
  pageUrl: string,
  currentPathname: string,
  currentQuery?: URLSearchParams | null
): boolean {
  const { path: pagePath, queryParams } = parsePageUrlParts(pageUrl);
  const normalizedPageUrl = pagePath.toLowerCase();
  const normalizedCurrentPath = normalizeRoutePath(currentPathname);

  const pathMatches =
    normalizedPageUrl === 'expenses/list'
      ? normalizedCurrentPath.startsWith('expenses')
      : normalizedCurrentPath === normalizedPageUrl ||
        normalizedCurrentPath.startsWith(`${normalizedPageUrl}/`);

  if (!pathMatches) {
    return false;
  }

  if (!queryParams) {
    return true;
  }

  if (
    normalizedPageUrl === 'expenses/list' &&
    normalizedCurrentPath !== 'expenses/list' &&
    normalizedCurrentPath !== 'expenses'
  ) {
    return false;
  }

  if (!currentQuery) {
    return false;
  }

  return Object.keys(queryParams).every((key) => currentQuery.has(key));
}

/**
 * AP sidebar groups — BE "Account Payable" only (all roles).
 * Plural "Accounts Payable" and FE legacy menus are not shown.
 */
export function getAccountsPayableNavGroups(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  _selectedRoleId: string | null | undefined,
  _bypassApManager1: boolean
): ApNavGroup[] {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  if (!usesApiPagePermissions(companyPermission)) {
    return [];
  }

  return getApNavGroupsFromApi(companyPermission);
}

/** First AP nav group (compat). Prefer {@link getAccountsPayableNavGroups}. */
export function getAccountsPayableNavGroup(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean
): ApNavGroup | null {
  return (
    getAccountsPayableNavGroups(
      storedCompanyPermissions,
      companyId,
      selectedRoleId,
      bypassApManager1
    )[0] ?? null
  );
}

export function hasAccountsPayableSidebar(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean
): boolean {
  return getAccountsPayableNavGroups(
    storedCompanyPermissions,
    companyId,
    selectedRoleId,
    bypassApManager1
  ).some((group) => group.subMenus.length > 0);
}

/**
 * All AP pageUrls from BE (singular + plural menus) for route access / titles.
 * Sidebar display uses {@link getAccountsPayableNavGroups} (singular only).
 */
export function getAccountsPayableSubMenus(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean
): CompanyPageSubMenu[] {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  if (usesApiPagePermissions(companyPermission)) {
    return getApPagePermissions(companyPermission).flatMap(
      (pagePermission) => pagePermission.subMenus ?? []
    );
  }

  // No BE pagePermissions → legacy role allowlist paths (guards / deep links).
  return getAccountsPayableSubMenusLegacy(selectedRoleId, bypassApManager1);
}

function filterVendorNavSubMenusForRole(
  subMenus: CompanyPageSubMenu[],
  selectedRoleId: string | null | undefined
): CompanyPageSubMenu[] {
  if (
    selectedRoleId &&
    VENDOR_TABS_HIDDEN_ROLE_IDS.includes(selectedRoleId)
  ) {
    return subMenus.filter((subMenu) => {
      const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
      return Object.prototype.hasOwnProperty.call(queryParams, 'myvendors');
    });
  }
  return subMenus;
}

function getVendorNavGroupFromApi(
  companyPermission: CompanyPermission | null | undefined,
  selectedRoleId: string | null | undefined
): VendorNavGroup | null {
  const pagePermission = getPagePermission(companyPermission, VENDORS_MENU);
  const subMenus = filterVendorNavSubMenusForRole(
    pagePermission?.subMenus ?? [],
    selectedRoleId
  );

  if (!subMenus.length) {
    return null;
  }

  return {
    menu: pagePermission?.menu ?? VENDORS_MENU,
    subMenus: sortVendorNavSubMenusForDesign(subMenus),
  };
}

function getVendorNavGroupLegacy(
  selectedRoleId: string | null | undefined
): VendorNavGroup | null {
  const subMenus = filterVendorNavSubMenusForRole(
    getVendorListTabSubMenus(null),
    selectedRoleId
  );

  if (!subMenus.length) {
    return {
      menu: VENDORS_MENU,
      subMenus: [{ page: 'Vendors', pageUrl: 'vendors/list?myvendors=' }],
    };
  }

  return {
    menu: VENDORS_MENU,
    subMenus: sortVendorNavSubMenusForDesign(subMenus),
  };
}

/** Vendors sidebar — BE pagePermissions with legacy fallback. */
export function getVendorNavGroup(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId?: string | null | undefined
): VendorNavGroup | null {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  if (usesApiPagePermissions(companyPermission)) {
    const fromApi = getVendorNavGroupFromApi(
      companyPermission,
      selectedRoleId
    );
    if (fromApi?.subMenus?.length) {
      return fromApi;
    }
  }

  return getVendorNavGroupLegacy(selectedRoleId);
}

export function hasVendorSidebar(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId?: string | null | undefined
): boolean {
  return Boolean(
    getVendorNavGroup(storedCompanyPermissions, companyId, selectedRoleId)
      ?.subMenus.length
  );
}
